import { type CSSProperties } from 'react'
import logoHouse480 from '../../../assets/logo-house-480.webp'
import logoHouse960 from '../../../assets/logo-house-960.webp'
import {
	getSceneVenues,
	HOUSE_SLOTS,
	hillPath,
	hillProfiles,
	KNOLL,
	MAX_SCENE_HOUSES,
	SCENE_NOTES as notes,
	SCENE_STARS as stars,
	type SceneVenue,
	venueHouseShape,
} from '#app/domains/program/pure'
import { useRevealOnce } from '#app/utils/motion.ts'

// the scene's geometry lives with the program, shared with the print poster
export { getSceneVenues, hillProfiles, type SceneVenue }

const HILL_PATHS = {
	narrow: hillPath(hillProfiles.narrow),
	wide: hillPath(hillProfiles.wide),
}

export function HeroScene({
	venues,
	variant = 'live',
	paused = false,
}: {
	venues: SceneVenue[]
	variant?: 'live' | 'gate'
	/** stop ambient loops while the hero is off screen */
	paused?: boolean
}) {
	const houses = venues.slice(0, MAX_SCENE_HOUSES)
	const [landRef, landReveal] = useRevealOnce<HTMLDivElement>({
		threshold: 0.6,
		rootMargin: '0px',
	})

	return (
		<div
			className="hk-scene"
			data-variant={variant}
			data-paused={paused ? '' : undefined}
			aria-hidden="true"
		>
			<svg className="hk-defs" width="0" height="0" focusable="false">
				<defs>
					<symbol id="hk-note-eighth" viewBox="0 0 24 24">
						<ellipse
							cx="8.4"
							cy="18.2"
							rx="4.3"
							ry="3.1"
							transform="rotate(-22 8.4 18.2)"
						/>
						<path
							d="M12.2 17.4V3.6c.9 3.2 5.6 3.9 5.1 8.6"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.9"
							strokeLinecap="round"
						/>
					</symbol>
					<symbol id="hk-note-beam" viewBox="0 0 24 24">
						<ellipse
							cx="5.6"
							cy="19"
							rx="3.7"
							ry="2.7"
							transform="rotate(-22 5.6 19)"
						/>
						<ellipse
							cx="17.6"
							cy="16.4"
							rx="3.7"
							ry="2.7"
							transform="rotate(-22 17.6 16.4)"
						/>
						<path
							d="M8.8 18.2V5.8M20.8 15.6V3.2"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.7"
							strokeLinecap="round"
						/>
						<path d="M8.8 5.3 20.8 2.7v3.2L8.8 8.5Z" />
					</symbol>
				</defs>
			</svg>

			<div className="hk-sky">
				<svg
					className="hk-stars"
					viewBox="0 0 100 62"
					preserveAspectRatio="xMidYMid slice"
				>
					{stars.map((star, i) => (
						<circle
							key={i}
							cx={star.x}
							cy={star.y}
							r={star.r / 10}
							className={star.twinkle ? 'hk-star hk-star-twinkle' : 'hk-star'}
							style={{ '--hk-delay': `${star.delay}s` } as CSSProperties}
						/>
					))}
				</svg>
			</div>

			<div className="hk-land" ref={landRef} data-reveal={landReveal}>
				<svg
					className="hk-hill"
					viewBox="0 0 100 30"
					preserveAspectRatio="none"
				>
					<path className="hk-hill-narrow" d={HILL_PATHS.narrow} />
					<path className="hk-hill-wide" d={HILL_PATHS.wide} />
				</svg>
				{houses.map((venue, i) => (
					<VenueHouse
						key={venue.key}
						narrowX={HOUSE_SLOTS.narrow[i]!}
						wideX={HOUSE_SLOTS.wide[i]!}
						windows={venue.concerts}
						flicker={variant === 'gate' && i === 1}
					/>
				))}
			</div>

			<div className="hk-art">
				<div className="hk-art-float">
					{/* warm light behind the house; the knoll stays a dark silhouette */}
					<div className="hk-glow" />
					{/* the knoll the festival house stands on, lit by the open door */}
					<svg
						className="hk-knoll"
						viewBox="0 0 400 120"
						preserveAspectRatio="none"
					>
						<defs>
							<radialGradient id="hk-knoll-light" cx="0.54" cy="0.14" r="0.24">
								<stop offset="0" stopColor="#f6d77a" stopOpacity="0.5" />
								<stop offset="1" stopColor="#f6d77a" stopOpacity="0" />
							</radialGradient>
						</defs>
						<path d={KNOLL} className="hk-knoll-ground" />
						{/* ground below the knoll, down past the hero's edge (under the
						    bottom bar on phones) */}
						<rect
							x="0"
							y="79.5"
							width="400"
							height="400"
							className="hk-knoll-ground"
						/>
						<path
							d={KNOLL}
							fill="url(#hk-knoll-light)"
							className="hk-knoll-lit"
						/>
					</svg>
					<div className="hk-house-wrap">
						<div className="hk-spill" />
						<img
							className="hk-house"
							src={logoHouse960}
							srcSet={`${logoHouse480} 480w, ${logoHouse960} 960w`}
							sizes="(min-width: 1024px) 380px, 58vw"
							width={960}
							height={1262}
							alt=""
							fetchPriority="high"
						/>
						<div className="hk-notes">
							{notes.map((note, i) => (
								<span
									key={i}
									className="hk-note"
									style={
										{
											left: `${note.x}%`,
											top: `${note.y}%`,
											width: `${note.size}px`,
											'--hk-drift': `${note.drift}px`,
											'--hk-rise': `${-note.rise}px`,
											'--hk-tilt': `${note.tilt}deg`,
											'--hk-dur': `${note.duration}s`,
											'--hk-delay': `${note.delay}s`,
										} as CSSProperties
									}
								>
									<svg viewBox="0 0 24 24">
										<use href={`#hk-note-${note.kind}`} />
									</svg>
								</span>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function VenueHouse({
	narrowX,
	wideX,
	windows,
	flicker,
}: {
	narrowX: number
	wideX: number
	windows: number[]
	flicker: boolean
}) {
	const { width, body, windows: panes } = venueHouseShape(windows.length)
	return (
		<svg
			className="hk-venue"
			viewBox={`0 0 ${width} 30`}
			style={
				{
					'--hk-x-narrow': narrowX,
					'--hk-y-narrow': hillProfiles.narrow(narrowX).toFixed(3),
					'--hk-x-wide': wideX,
					'--hk-y-wide': hillProfiles.wide(wideX).toFixed(3),
					width: `calc(var(--hk-venue-w) * ${width / 24})`,
				} as CSSProperties
			}
		>
			<path className="hk-venue-body" d={body} />
			{panes.map((pane, i) => {
				const order = windows[i]
				const lit = order != null
				return (
					<rect
						key={i}
						x={pane.x}
						y={pane.y}
						width={5}
						height={6}
						rx={0.6}
						className={
							flicker && i === 0
								? 'hk-window hk-window-flicker'
								: lit
									? 'hk-window hk-window-lit'
									: 'hk-window'
						}
						style={lit ? ({ '--hk-order': order } as CSSProperties) : undefined}
					/>
				)
			})}
		</svg>
	)
}
